// @ts-nocheck
import {
    Button,
    CheckboxGroup,
    GridContainer,
    GridItem,
    Icon,
    LoadingSpinner,
    TextField,
    Typography,
} from "@insite/mobius/index";
import Colors from "@store/Themes/common/styleguide/config/colors";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import translate from "@insite/client-framework/Translate";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Search from "@insite/mobius/Icons/Search";
import Popover from "@insite/mobius/Popover";
import getColor from "@insite/mobius/utilities/getColor";
import CheckboxModalPicker from "@store/Library/Components/Reporting/ModalPicker/CheckboxModalPicker";
import { useDebounce } from "@store/Framework/Common/Hooks/useDebounce";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";

const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 600, last: false });
const StyledGridItem = styled(GridItem)<{ isMaxWidth: boolean }>`
    && {
        display: block;
        max-width: ${props => (props.isMaxWidth ? "100%" : "400px !important")};
    }
`;
interface RenderItemProps {
    po: string;
    selectedItems: string[];
    toggleItem: (item: string) => void;
}
const RenderItem: React.FC<RenderItemProps> = ({ po: itemText, selectedItems, toggleItem }) => {
    return (
        <CheckboxModalPicker key={itemText} itemText={itemText} selectedItems={selectedItems} toggleItem={toggleItem} />
    );
};
const SpinnerWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 15vh;
    width: 100%;
`;
interface ListDropdownProps {
    isError: boolean;
}

const ListDropdown = styled(Button)<ListDropdownProps>`
    && {
        display: block;
        width: 100%;
        padding: 0 10px;
        ${mobileQuery} {
            padding: 0.5rem;
        }
        background-color: white;
        color: ${getColor("text.main")};
        font-weight: 400;
        border: ${p => (p.isError ? `2px solid ${Colors?.danger?.main}` : `1px solid${Colors?.common?.borderDark}`)};
        min-height: ${pxToRem(44)} !important;
        margin-top: ${pxToRem(5)};
        text-align: left;
        font-size: 1rem;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
`;

const ListDropdownIcon = styled(Icon)`
    && {
        margin-left: auto;
    }
`;

const Badge = styled(Typography)`
    && {
        display: flex;
        padding: 3px 9px 3px 8px;
        margin-left: 0.75rem;
        margin-right: 0.75rem;
        letter-spacing: -0.025em;
        background-color: ${getColor("common.checkboxPickerBackground")};
        border-radius: 41%;
        color: ${getColor("secondary.main")};
        font-weight: 900;
        text-align: center;
        font-size: 0.75rem;
        align-items: center;
        justify-content: center;
    }
`;

const PickerLabel = styled(Typography)`
    && {
        font-size: 1rem;
        text-transform: none;
        color: ${getColor("text.main")};
        min-height: 1.375rem;
        display: inline-block;
        width: 100%;
        font-weight: 700;
        line-height: 1.375;
        padding-bottom: 4px;
    }
`;

const PopoverPicker = styled(Popover)`
    && {
        overflow-y: auto !important;
        max-height: 400px;
    }
`;

const PickerWrapper = styled.div`
    padding: 0.5rem 1.25rem 1.25rem 1.25rem;
`;

const PickerHeader = styled.div`
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    margin-bottom: 1rem;
`;

const SelectAllButton = styled(Button)`
    && {
        min-width: auto;
        min-height: auto;
        background-color: transparent !important;
        border: none !important;
        color: ${getColor("primary")};
        padding: 0;
        font-weight: 400;
        & span {
            font-size: 0.9rem !important;
            text-wrap: nowrap;
        }
    }
`;
const StyledSpan = styled.span`
    font-weight: 700;
    font-style: italic;
    margin-left: 0.5em;
    font-size: 0.8125rem;
`;
const SearchModalPicker = styled.div`
    & span {
        width: 36px;
        height: 36px;
    }
    padding-bottom: 0.5rem;
`;
interface Props {
    header: string;
    onOpen?: () => Promise<void>;
    error?: boolean;
    toggleAllItems: (itemIds: string[]) => void;
    data: string[];
    selectedItems: string[];
    isLoading?: boolean;
    toggleItem: (item: string) => void;
    hideHeader?: boolean;
    emptyMessage?: string;
    required?: boolean;
    customPlaceholder?: string;
}
const ModalPicker: React.FC<Props> = ({
    onOpen = () => Promise.resolve(),
    header,
    error = false,
    toggleAllItems,
    data,
    selectedItems,
    isLoading,
    toggleItem,
    hideHeader = false,
    emptyMessage = "No items found",
    required = false,
    customPlaceholder,
}) => {
    const [filteredData, setFilteredData] = React.useState(data);
    const [searchTerm, setSearchTerm] = useState("");
    const handleSearch = (newSearchTerm: string) => {
        if (!newSearchTerm) {
            setFilteredData(data);
            return;
        }
        setFilteredData(data.filter(data => data.toLowerCase().includes(newSearchTerm.toLowerCase())));
    };
    const debouncedSearch = useDebounce(handleSearch, 300);

    const selectCount = () => {
        const total = selectedItems.length;

        if (total > 0) {
            const amountBadge = <Badge variant="legend">{total}</Badge>;
            return amountBadge;
        }
    };

    useEffect(() => {
        debouncedSearch(searchTerm);
    }, [data, setFilteredData, searchTerm]);

    const popover = useRef<HTMLDivElement>(null);
    const popoverTrigger = (
        <ListDropdown isError={error}>
            {customPlaceholder ?? header}
            {selectCount()}
            <ListDropdownIcon src={ChevronDown} />
        </ListDropdown>
    );

    return (
        <GridContainer onClick={event => event.preventDefault()}>
            <StyledGridItem isMaxWidth width={[12, 12, 12, 12, 12]}>
                {!hideHeader && (
                    <PickerLabel variant="legend">
                        {header} {required && <StyledSpan>{translate("required *")}</StyledSpan>}
                    </PickerLabel>
                )}
                <PopoverPicker
                    contentBodyProps={{
                        as: "div",
                    }}
                    positionFunction={() => {
                        return {
                            position: "absolute",
                            left: 0,
                            width: "100%",
                        };
                    }}
                    hasChildPortal={true}
                    insideRefs={[popover]}
                    onOpen={onOpen}
                    popoverTrigger={popoverTrigger}
                >
                    <PickerWrapper ref={popover}>
                        <PickerHeader>
                            <Typography
                                variant="h4"
                                css={css`
                                    margin-bottom: 0;
                                    font-size: 1.25rem !important;
                                `}
                            >
                                {header}
                            </Typography>
                            <SelectAllButton onClick={() => toggleAllItems(data)}>
                                {data.length === selectedItems.length
                                    ? translate("Deselect All")
                                    : translate("Select All")}
                            </SelectAllButton>
                        </PickerHeader>

                        <SearchModalPicker>
                            <TextField
                                value={searchTerm}
                                onChange={event => setSearchTerm(event.target.value)}
                                iconProps={{ src: Search }}
                                placeholder={translate("search")}
                                css={css`
                                    height: 40px !important;
                                    font-size: 0.9rem !important;
                                `}
                            ></TextField>
                        </SearchModalPicker>
                        {isLoading ? (
                            <SpinnerWrapper>
                                <LoadingSpinner />
                            </SpinnerWrapper>
                        ) : (
                            <CheckboxGroup>
                                {filteredData.length === 0 && <Typography>{emptyMessage}</Typography>}
                                {filteredData.map(po => RenderItem({ po, selectedItems, toggleItem }))}
                            </CheckboxGroup>
                        )}
                    </PickerWrapper>
                </PopoverPicker>
            </StyledGridItem>
        </GridContainer>
    );
};

export default ModalPicker;
